import {
  Component,
  OnDestroy,
  OnInit,
  DOCUMENT,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { filter, map, skipWhile, take, takeUntil } from 'rxjs/operators';
import { combineLatest, distinctUntilChanged, fromEvent, Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';

import { configurationSelectors, settingsSelectors, State, UserType } from '@wam/shared';
import { TranslateService } from '@ngx-translate/core';
import { isNil, isString } from 'lodash-es';
import { authenticationSelectors } from '@wam/authentication';
import packageJson from '../../package.json';

import { Layouts, LayoutService } from '@app/layouts/layout.service';
import { ViewportScroller, AsyncPipe } from '@angular/common';
import * as Sentry from '@sentry/angular';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SnowplowService } from '@app/shared/snowplow/snowplow.service';
import { NgProgressbar } from 'ngx-progressbar';
import { NgProgressHttp } from 'ngx-progressbar/http';
import { NotificationsComponent } from './shared/notifications/notifications.component';
import { DefaultLayoutComponent } from './layouts/default-layout/default-layout.component';
import { ScrollToTopComponent } from './shared/scroll-to-top/scroll-to-top.component';
import { GlobalProgressBarDirective } from './shared/progress-bar/global-progress-bar.directive';
import { DeploymentUpdateService } from './shared/deployment-update/deployment-update.service';

export const shouldInitializeSentry = (
  isConfigurationEnabled: boolean,
  isLocalStorageEnabled: boolean,
  environment: string,
): boolean => (environment !== 'local' && isConfigurationEnabled) || isLocalStorageEnabled;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgProgressbar,
    NgProgressHttp,
    GlobalProgressBarDirective,
    NotificationsComponent,
    DefaultLayoutComponent,
    ScrollToTopComponent,
    RouterOutlet,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  private translate = inject(TranslateService);
  private document = inject<Document>(DOCUMENT);
  private viewport = inject(ViewportScroller);
  private layoutService = inject(LayoutService);
  private router = inject(Router);
  private snowplowService = inject(SnowplowService);
  private readonly deploymentUpdates = inject(DeploymentUpdateService);

  private destroyed$ = new Subject<void>();
  showScroll$: Observable<boolean> = fromEvent(this.document, 'scroll').pipe(
    takeUntil(this.destroyed$),
    map(() => this.viewport.getScrollPosition()?.[1] > 0),
  );

  Layouts = Layouts;
  layout$: Observable<Layouts> = this.layoutService.layout$.pipe(distinctUntilChanged());

  ngOnInit(): void {
    this.deploymentUpdates.start();
    this.initializeSentry();
    this.initializeTranslations();
    this.initializeSnowplow();
    this.trackPageView();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  initializeSnowplow(): void {
    this.snowplowService.initialize();
  }

  trackPageView(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.snowplowService.trackPageView();
      }
    });
  }

  initializeSentry(): void {
    const isLocalStorageEnabled = localStorage.getItem('sentryEnabled') === 'true';
    combineLatest([
      this.store.pipe(select(configurationSelectors.isEnabled('SENTRY'))),
      this.store.pipe(select(settingsSelectors.getSentrySettings)),
      this.store
        .pipe(select(authenticationSelectors.getCurrentUser))
        .pipe(filter((user) => !isNil(user))),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isConfigurationEnabled, settings, user]) => {
        if (
          shouldInitializeSentry(
            isConfigurationEnabled,
            isLocalStorageEnabled,
            settings.environment,
          )
        ) {
          Sentry.init({
            dsn: settings.dsn,
            environment: settings.environment,
            release: packageJson.version,
            tracesSampleRate: settings.tracesSampleRate,
            integrations: [Sentry.browserTracingIntegration()],
            initialScope: {
              tags: {
                application: user.application,
                organization:
                  user.organization ?? (user.type === UserType.ADMIN ? 'admin' : undefined),
              },
              user: {
                id: user.uuid ?? user.sub,
                email: user.email,
                username: user.name,
              },
            },
            beforeSend: (event) => {
              if (isNil(event.message) || isString(event.message)) {
                return event;
              }
              return null;
            },
          });
        }
      });
  }

  private initializeTranslations(): void {
    this.translate.setFallbackLang('en');
    this.store
      .select(configurationSelectors.getUserConfiguration<string>('language'))
      .pipe(skipWhile(isNil), take(1))
      .subscribe((language: string) => {
        const cleanLanguage = language.replace(/^['"]|['"]$/g, '');
        this.translate.use(cleanLanguage);
      });
  }

  onScrollToTop(): void {
    this.viewport.scrollToPosition([0, 0]);
  }
}
